/**
 * Network Monitor Service
 * Enhanced network monitoring using Capacitor Network plugin
 */

import { Network, ConnectionStatus } from '@capacitor/network';
import { BehaviorSubject, Observable } from 'rxjs';
import logger from '../../utils/logger';

export interface NetworkState {
  connected: boolean;
  connectionType: string;
  connectionQuality: 'good' | 'fair' | 'poor' | 'unknown';
  lastOnline?: number;
  lastOffline?: number;
}

class NetworkMonitorService {
  private networkState$ = new BehaviorSubject<NetworkState>({
    connected: true,
    connectionType: 'unknown',
    connectionQuality: 'unknown'
  });

  private listeners: Array<() => void> = [];
  private isInitialized = false;

  /**
   * Initialize network monitoring
   */
  async init(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Get initial network status
      const status = await Network.getStatus();
      this.updateNetworkState(status);

      // Listen for network changes
      const handler = await Network.addListener('networkStatusChange', (status) => {
        this.updateNetworkState(status);
      });

      this.listeners.push(() => handler.remove());
      this.isInitialized = true;

      logger.info('Network monitor initialized');
    } catch (error) {
      logger.error('Failed to initialize network monitor:', error);
      // Fall back to browser API
      this.initBrowserNetworkMonitoring();
    }
  }

  /**
   * Initialize browser-based network monitoring as fallback
   */
  private initBrowserNetworkMonitoring(): void {
    const updateStatus = () => {
      const connected = navigator.onLine;
      const state = this.networkState$.value;
      
      if (state.connected !== connected) {
        this.networkState$.next({
          ...state,
          connected,
          connectionType: connected ? 'unknown' : 'none',
          lastOnline: connected ? Date.now() : state.lastOnline,
          lastOffline: connected ? state.lastOffline : Date.now()
        });
      }
    };

    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);

    this.listeners.push(() => {
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);
    });

    // Set initial state
    updateStatus();
  }

  /**
   * Update network state from Capacitor status
   */
  private updateNetworkState(status: ConnectionStatus): void {
    const state = this.networkState$.value;
    const connected = status.connected;
    
    // Determine connection quality based on type
    let connectionQuality: NetworkState['connectionQuality'] = 'unknown';
    if (status.connectionType === 'wifi') {
      connectionQuality = 'good';
    } else if (status.connectionType === 'cellular') {
      connectionQuality = 'fair';
    } else if (status.connectionType === 'none') {
      connectionQuality = 'poor';
    }

    this.networkState$.next({
      connected,
      connectionType: status.connectionType || 'unknown',
      connectionQuality,
      lastOnline: connected ? Date.now() : state.lastOnline,
      lastOffline: connected ? state.lastOffline : Date.now()
    });

    logger.info(`Network status changed: ${connected ? 'online' : 'offline'} (${status.connectionType})`);
  }

  /**
   * Get current network state
   */
  get currentState(): NetworkState {
    return this.networkState$.value;
  }

  /**
   * Get network state as observable
   */
  get state$(): Observable<NetworkState> {
    return this.networkState$.asObservable();
  }

  /**
   * Check if currently online
   */
  get isOnline(): boolean {
    return this.networkState$.value.connected;
  }

  /**
   * Check if connection is good enough for sync
   */
  get canSync(): boolean {
    const state = this.networkState$.value;
    return state.connected && state.connectionQuality !== 'poor';
  }

  /**
   * Wait for network to come online
   */
  waitForConnection(): Promise<void> {
    return new Promise((resolve) => {
      if (this.isOnline) {
        resolve();
        return;
      }

      const subscription = this.networkState$.subscribe((state) => {
        if (state.connected) {
          subscription.unsubscribe();
          resolve();
        }
      });
    });
  }

  /**
   * Get time since last online (in ms)
   */
  get timeSinceLastOnline(): number | null {
    const { lastOnline, connected } = this.networkState$.value;
    if (connected || !lastOnline) return null;
    return Date.now() - lastOnline;
  }

  /**
   * Clean up listeners
   */
  destroy(): void {
    this.listeners.forEach(remove => remove());
    this.listeners = [];
    this.isInitialized = false;
  }
}

// Export singleton instance
const networkMonitorService = new NetworkMonitorService();
export default networkMonitorService;